import React, { useEffect, useState } from 'react';
import { X, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ImportRow, ImportResult } from '../../../services/whatsapp/contactService';

interface ImportContactsDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (rows: ImportRow[]) => Promise<ImportResult>;
}

/**
 * Splits one CSV line, honouring quoted cells.
 *
 * Deliberately minimal rather than pulling in a parser: the import format is
 * two columns, and a dependency for that would be more risk than it removes.
 */
const splitCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      // A doubled quote inside a quoted cell is a literal quote.
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((c) => c.trim());
};

/**
 * Parses pasted or uploaded CSV into rows.
 *
 * Accepts either a header row naming the columns, or bare `phone,name` lines.
 */
const parseCsv = (text: string): ImportRow[] => {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) return [];

  const first = splitCsvLine(lines[0]).map((c) => c.toLowerCase());
  const hasHeader = first.some((c) => /phone|number|mobile|name/.test(c));

  let phoneIndex = 0;
  let nameIndex = 1;

  if (hasHeader) {
    const foundPhone = first.findIndex((c) => /phone|number|mobile/.test(c));
    const foundName = first.findIndex((c) => /name/.test(c));
    if (foundPhone >= 0) phoneIndex = foundPhone;
    if (foundName >= 0) nameIndex = foundName;
  }

  return lines
    .slice(hasHeader ? 1 : 0)
    .map((line) => {
      const cells = splitCsvLine(line);
      return { phone: cells[phoneIndex] || '', name: cells[nameIndex] || '' };
    })
    .filter((row) => row.phone);
};

const ImportContactsDialog: React.FC<ImportContactsDialogProps> = ({
  open,
  onClose,
  onImport
}) => {
  const [text, setText] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setText('');
      setResult(null);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const rows = parseCsv(text);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setText(await file.text());
    event.target.value = '';
  };

  const handleImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      setResult(await onImport(rows));
      setText('');
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Import contacts
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {result ? (
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm font-medium text-success-600">
                <CheckCircle2 className="h-4 w-4" />
                Imported {result.created} contact
                {result.created === 1 ? '' : 's'}
              </p>
              {result.skipped > 0 && (
                <p className="text-xs text-gray-500">
                  {result.skipped} already existed and were left unchanged.
                </p>
              )}
              {result.invalid.length > 0 && (
                <div className="rounded bg-warning-50 p-2">
                  <p className="text-xs font-medium text-warning-700">
                    {result.invalid.length} row
                    {result.invalid.length === 1 ? '' : 's'} skipped:
                  </p>
                  <ul className="mt-1 max-h-32 overflow-y-auto text-[11px] text-warning-700">
                    {result.invalid.slice(0, 20).map((row) => (
                      <li key={row.row}>
                        Row {row.row}: “{row.phone}” — {row.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 p-4 text-sm text-gray-600 hover:border-primary-500 hover:text-primary-600">
                <Upload className="h-4 w-4" />
                Choose a CSV file
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  className="hidden"
                  onChange={handleFile}
                />
              </label>

              <div className="text-center text-xs text-gray-400">or paste below</div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                placeholder={'phone,name\n919876543210,Asha Kumar\n919812345678,Ravi Singh'}
                className="w-full resize-none rounded-lg border-gray-300 font-mono text-xs focus:border-primary-500 focus:ring-primary-500"
              />

              <p className="text-[11px] text-gray-500">
                A header row is detected automatically. Without one, the first
                column is the number and the second the name. Up to 1000 rows per
                import; duplicates are skipped.
              </p>

              <p className="flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-black">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                Imported contacts start as opt-in <strong>unknown</strong>. Only
                import numbers that have consented to hear from you — Meta
                penalises unsolicited messaging.
              </p>

              {error && <p className="text-xs text-danger-600">{error}</p>}

              <button
                type="button"
                onClick={handleImport}
                disabled={importing || rows.length === 0}
                className="w-full rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {importing
                  ? 'Importing…'
                  : rows.length > 0
                    ? `Import ${rows.length} contact${rows.length === 1 ? '' : 's'}`
                    : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportContactsDialog;
